/**
 * NotificationDrawer — right-anchored sheet that surfaces background
 * events (datasheet extraction complete, post-upload diff summaries,
 * future: org invites, plugin connection status). Mounted globally
 * at AppLayout root.
 *
 * Visual:
 *   * Right-anchored MUI Drawer, 360px wide, dark surface, hairline
 *     border on the left edge to read as "extension of the rack" not
 *     "popover".
 *   * Each row: severity dot + mono timestamp on top, title + body
 *     stacked below, optional one-click action button on the right.
 *   * Empty state uses the brand BlueprintEmptyState pattern.
 *
 * Mark-all-as-seen button in the header. Clear-all in the footer
 * with a confirm-by-second-click — single click flashes the button
 * red, second click within 3s wipes the list.
 */
import { BlueprintEmptyState, ClockMotif } from "@components/EmptyState";
import {
  toFeedNotification,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useServerNotifications,
} from "@features/notifications";
import type { FeedNotification } from "@features/notifications";
import CloseIcon from "@mui/icons-material/CloseRounded";
import DoneAllIcon from "@mui/icons-material/DoneAllRounded";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useNotifications } from "@stores/notificationStore";
import type { Notification } from "@stores/notificationStore";
import { useCallback, useEffect, useMemo, useState } from "react";

export function NotificationDrawer() {
  const drawerOpen = useNotifications((s) => s.drawerOpen);
  const closeDrawer = useNotifications((s) => s.closeDrawer);
  const clientNotifications = useNotifications((s) => s.notifications);
  const markAllSeenClient = useNotifications((s) => s.markAllSeen);
  const markSeenClient = useNotifications((s) => s.markSeen);
  const clearAllClient = useNotifications((s) => s.clearAll);
  const [confirmClear, setConfirmClear] = useState(false);

  // Server-persisted billing/account alerts.
  const { data: serverFeed } = useServerNotifications();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  // CTA navigation for server rows — full nav to the internal path
  // (billing lives behind auth so a hard nav is fine) after closing.
  const handleAction = useCallback(
    (url: string) => {
      closeDrawer();
      window.location.assign(url);
    },
    [closeDrawer],
  );

  // Merge server + client into one time-ordered list. Server rows carry
  // an `origin: "server"` marker so mark-read routes to the backend.
  const notifications: FeedNotification[] = useMemo(() => {
    const server = (serverFeed?.items ?? []).map((n) =>
      toFeedNotification(n, handleAction),
    );
    const client: FeedNotification[] = clientNotifications.map((n) => ({
      ...n,
      origin: "client",
    }));
    return [...server, ...client].sort(
      (a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime(),
    );
  }, [serverFeed, clientNotifications, handleAction]);

  const serverUnread = serverFeed?.unread_count ?? 0;

  const markSeen = useCallback(
    (row: FeedNotification) => {
      if (row.origin === "server" && row.serverId) {
        if (!row.seen) markRead.mutate(row.serverId);
      } else {
        markSeenClient(row.id);
      }
    },
    [markRead, markSeenClient],
  );

  const markAllSeen = useCallback(() => {
    markAllSeenClient();
    if (serverUnread > 0) markAllRead.mutate();
  }, [markAllSeenClient, markAllRead, serverUnread]);

  // Auto-mark-seen when the drawer opens — user has clearly seen them.
  useEffect(() => {
    if (drawerOpen) markAllSeen();
  }, [drawerOpen, markAllSeen]);

  // Reset the clear-all confirm flag if the user wanders away.
  useEffect(() => {
    if (!confirmClear) return;
    const id = window.setTimeout(() => setConfirmClear(false), 3000);
    return () => window.clearTimeout(id);
  }, [confirmClear]);

  return (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={closeDrawer}
      PaperProps={{
        sx: (t) => ({
          width: { xs: "100%", sm: 380 },
          bgcolor: t.palette.surface.default,
          borderLeft: `1px solid ${t.palette.border.default}`,
          display: "flex",
          flexDirection: "column",
          // Hairline trace-cyan rim — applied as a direct inset
          // boxShadow on the Paper rather than a `::before` pseudo so
          // we don't override MUI's `position: fixed` anchoring on
          // the Paper. (Setting `position: relative` here detaches
          // the drawer from the right edge and lets it stretch full-
          // width — that was a real bug worth a comment.)
          boxShadow: `inset 0 0 0 1px ${alpha(t.palette.primary.main, 0.06)}`,
        }),
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={(t) => ({
          px: 1.75,
          py: 1.25,
          borderBottom: `1px solid ${alpha(t.palette.primary.main, 0.12)}`,
        })}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={(t) => ({
              fontFamily: t.fonts.mono,
              fontSize: "0.625rem",
              fontWeight: 500,
              color: "text.secondary",
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              lineHeight: 1,
            })}
          >
            Activity
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mt: 0.5,
              fontWeight: 700,
              fontSize: "1rem",
              letterSpacing: "-0.012em",
            }}
          >
            Notifications
          </Typography>
        </Box>
        {notifications.some((n) => !n.seen) && (
          <Tooltip title="Mark all as read">
            <IconButton size="small" onClick={markAllSeen}>
              <DoneAllIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <IconButton size="small" onClick={closeDrawer}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {notifications.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <BlueprintEmptyState
              motif={ClockMotif}
              eyebrow="ALL CLEAR"
              headline="No notifications"
              body="Background events (datasheet extraction, uploads, sync alerts) appear here as they happen."
            />
          </Box>
        ) : (
          notifications.map((n) => (
            <NotificationRow key={n.id} notif={n} onClick={() => markSeen(n)} />
          ))
        )}
      </Box>

      {/* Footer */}
      {notifications.length > 0 && (
        <Box
          sx={(t) => ({
            px: 1.75,
            py: 1.25,
            borderTop: `1px solid ${alpha(t.palette.primary.main, 0.12)}`,
          })}
        >
          <Button
            size="small"
            variant="text"
            color={confirmClear ? "error" : "inherit"}
            onClick={() => {
              if (confirmClear) {
                // Client rows are dropped; server rows have no delete
                // endpoint, so "clear" marks them read (they leave the
                // unread rail and stop lighting the bell).
                clearAllClient();
                if (serverUnread > 0) markAllRead.mutate();
                setConfirmClear(false);
              } else {
                setConfirmClear(true);
              }
            }}
            sx={(t) => ({
              fontFamily: t.fonts.mono,
              fontSize: "0.625rem",
              fontWeight: 500,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: confirmClear ? "error.main" : "text.secondary",
              "&:hover": {
                color: confirmClear ? "error.main" : "primary.light",
                bgcolor: alpha(
                  confirmClear ? t.palette.error.main : t.palette.primary.main,
                  0.06,
                ),
              },
            })}
          >
            {confirmClear ? "Click again to confirm" : "Clear all"}
          </Button>
        </Box>
      )}
    </Drawer>
  );
}

// ────────────────────────────────────────────────────────────────────────────

const SEVERITY_COLOR: Record<Notification["severity"], string> = {
  info: "primary.main",
  success: "success.main",
  warning: "warning.main",
  error: "error.main",
};

/** Pull the raw hex for a severity tone so we can compose the alpha
 *  ring around the severity dot. The severity → palette path is
 *  duplicated from `SEVERITY_COLOR` to avoid string-parsing the
 *  "x.main" tokens at runtime. */
function getSeverityHex(
  t: import("@mui/material/styles").Theme,
  severity: Notification["severity"],
): string {
  switch (severity) {
    case "success":
      return t.palette.success.main;
    case "warning":
      return t.palette.warning.main;
    case "error":
      return t.palette.error.main;
    default:
      return t.palette.primary.main;
  }
}

function NotificationRow({
  notif,
  onClick,
}: {
  notif: Notification;
  onClick: () => void;
}) {
  return (
    <Box
      onClick={onClick}
      sx={(t) => ({
        position: "relative",
        px: 1.75,
        py: 1.25,
        cursor: "pointer",
        borderBottom: `1px solid ${alpha(t.palette.divider, 0.6)}`,
        transition: `background-color ${t.motion.fast}`,
        bgcolor: notif.seen
          ? "transparent"
          : alpha(t.palette.primary.main, 0.04),
        "&:hover": { bgcolor: alpha(t.palette.primary.main, 0.08) },
        // Trace-cyan side-bar on unseen rows so the user can scan unread
        // items without re-reading every row. Slightly thicker (3px) to
        // match the editorial inset-rim weight used elsewhere.
        ...(!notif.seen && {
          "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 3,
            background: t.palette.primary.main,
            boxShadow: `0 0 8px ${alpha(t.palette.primary.main, 0.5)}`,
          },
        }),
      })}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        spacing={1}
        sx={{ minWidth: 0 }}
      >
        <Box
          sx={(t) => ({
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: SEVERITY_COLOR[notif.severity],
            boxShadow: `0 0 0 2px ${alpha(getSeverityHex(t, notif.severity), 0.18)}`,
            flexShrink: 0,
            mt: 0.875,
          })}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Mono kicker timestamp — matches sidebar cluster headers
              and project-card eyebrow vocabulary. */}
          <Typography
            sx={(t) => ({
              fontFamily: t.fonts.mono,
              fontSize: "0.625rem",
              fontWeight: 500,
              color: "text.secondary",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              display: "block",
              lineHeight: 1,
              mb: 0.625,
            })}
          >
            {formatRelative(notif.ts)}
          </Typography>

          {/* Title — punchier h-treatment so it scans before the
              body. Heavier when unseen so unread rows lead. */}
          <Typography
            sx={{
              color: "text.primary",
              fontWeight: notif.seen ? 500 : 700,
              fontSize: "0.875rem",
              letterSpacing: "-0.005em",
              lineHeight: 1.3,
            }}
          >
            {notif.title}
          </Typography>

          {/* Body — secondary tone, slightly breathy line-height so
              two- to three-line messages read comfortably. */}
          {notif.body && (
            <Typography
              sx={{
                color: "text.secondary",
                fontSize: "0.75rem",
                lineHeight: 1.5,
                mt: 0.5,
              }}
            >
              {notif.body}
            </Typography>
          )}

          {/* Action — mono ALL-CAPS pill matching the "Clear all"
              footer vocabulary, trace-cyan hover. */}
          {notif.action && (
            <Button
              size="small"
              variant="text"
              onClick={(e) => {
                e.stopPropagation();
                notif.action?.onClick();
              }}
              sx={(t) => ({
                mt: 1,
                ml: -0.75,
                px: 0.875,
                py: 0.25,
                fontFamily: t.fonts.mono,
                fontSize: "0.625rem",
                fontWeight: 600,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "primary.light",
                minWidth: 0,
                "&:hover": {
                  color: "primary.light",
                  bgcolor: alpha(t.palette.primary.main, 0.1),
                },
              })}
            >
              {notif.action.label}
            </Button>
          )}
        </Box>
      </Stack>
    </Box>
  );
}

function formatRelative(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const seconds = Math.floor((Date.now() - t) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
